// Focused multi-branch (head+body) mesh loader for the live OGS-streaming client.
//
// The streaming client runs NO in-browser inference — the server streams geom/SH
// per frame and the client only lifts UV→3D. So unlike the donor's full
// `loadScene` (which also resolves geom/appr/rig2mesh ONNX + hair + single-region),
// the streaming path needs ONLY the static UV geometry per branch (idxim, barim,
// triim, valid, faces) used by `StreamLiftGenerator`'s pass1 lift. This loader is
// deliberately scoped to that, leaving the existing single-region `loader.ts`
// (the Isaac path) untouched.
//
// Asset layout (resolved from the character's asset-manager `/ogs` bundle via the
// resolver passed in — no bundled public/ copy. The GPU server pulls its decoders
// from that same collection, so both halves are always the same build):
//   scene.json           — { branches: { head:{mesh_json,mesh_bin,uv_res,…}, body:{…} } }
//   mesh_{head,body}.json — MeshManifest (uv_res, num_vertices, num_faces, buffers[])
//   mesh_{head,body}.bin  — packed faces/idxim/barim/triim/valid/neutral_vertices

export type DType = "f32" | "i32" | "u32" | "u8";

export interface BufferEntry {
  name: string;
  dtype: DType;
  shape: number[];
  offset: number;
  byte_length: number;
}

export interface MeshManifest {
  uv_res: number;
  num_vertices: number;
  num_faces: number;
  buffers: BufferEntry[];
}

export interface MeshAssets {
  manifest: MeshManifest;
  faces: Uint32Array;            // (F, 3)
  idxim: Int32Array;             // (H, W, 3)
  barim: Float32Array;           // (H, W, 3)
  triim: Int32Array;             // (H, W)
  valid: Uint8Array;             // (H, W)
  neutralVertices: Float32Array; // (V, 3)
}

export interface StreamBranch {
  manifest: MeshAssets;
  /** rig-driven (live verts from rig2mesh, primary wire slot) vs baked secondary. */
  rigDriven: boolean;
}

export interface StreamScene {
  subject: string;
  mode: "single_region" | "multi_region";
  /** Branch name → mesh. Single-region has one entry (the rig-driven branch). */
  branches: Record<string, StreamBranch>;
  /** Wire-slot order: [primary] or [primary, secondary]. The primary branch maps to
   *  the head wire slot (verts tag 1/7), the secondary to the body slot (tag 3/8). */
  order: string[];
}

function view(blob: ArrayBuffer, e: BufferEntry) {
  switch (e.dtype) {
    case "f32": return new Float32Array(blob, e.offset, e.byte_length / 4);
    case "i32": return new Int32Array(blob, e.offset, e.byte_length / 4);
    case "u32": return new Uint32Array(blob, e.offset, e.byte_length / 4);
    case "u8":  return new Uint8Array(blob, e.offset, e.byte_length);
  }
}

function decodeMeshAssets(json: MeshManifest, bin: ArrayBuffer): MeshAssets {
  const byName = Object.fromEntries(json.buffers.map((b) => [b.name, b]));
  return {
    manifest: json,
    faces: view(bin, byName.faces) as Uint32Array,
    idxim: view(bin, byName.idxim) as Int32Array,
    barim: view(bin, byName.barim) as Float32Array,
    triim: view(bin, byName.triim) as Int32Array,
    valid: view(bin, byName.valid) as Uint8Array,
    neutralVertices: view(bin, byName.neutral_vertices) as Float32Array,
  };
}

/** Fetch one bundle file. `size` is the /ogs row's byte size, used by the asset
 *  cache as a version key. Defaults to plain `fetch`; StreamAvatarModel injects
 *  `cachedFetchAamFile` so the streaming path shares ONE URL policy (and the
 *  Cache-Storage layer + boot-progress metering) with the on-device /ogs path
 *  instead of building its own URLs. */
export type AssetFetcher = (url: string, size?: number) => Promise<Response>;

const plainFetch: AssetFetcher = (url) => fetch(url);

async function fetchJson(f: AssetFetcher, url: string, size?: number): Promise<any> {
  const r = await f(url, size);
  if (!r.ok) throw new Error(`fetch ${url}: ${r.status}`);
  return r.json();
}

async function fetchBin(f: AssetFetcher, url: string, size?: number): Promise<ArrayBuffer> {
  const r = await f(url, size);
  if (!r.ok) throw new Error(`fetch ${url}: ${r.status}`);
  return r.arrayBuffer();
}

/** Resolve a sub-asset filename (as named in scene.json, e.g. "mesh.json") to a
 *  fetchable URL. Streaming scenes are always server-sourced (per-file AAM rows,
 *  whose filenames don't share a directory prefix), so the caller MUST supply
 *  this map — there is no bundled/local fallback. */
export type AssetUrlResolver = (filename: string) => string;

async function loadMesh(
  resolve: AssetUrlResolver, opts: StreamSceneOptions, meshJson: string, meshBin: string,
): Promise<MeshAssets> {
  const f = opts.fetchAsset ?? plainFetch;
  const sizeOf = opts.sizeOf ?? (() => undefined);
  const [json, bin] = await Promise.all([
    fetchJson(f, resolve(meshJson), sizeOf(meshJson)) as Promise<MeshManifest>,
    fetchBin(f, resolve(meshBin), sizeOf(meshBin)),
  ]);
  return decodeMeshAssets(json, bin);
}

export interface StreamSceneOptions {
  /** Fetch a large, size-versioned binary (the mesh json/bin). Omit for plain
   *  `fetch` (the unit-test path). */
  fetchAsset?: AssetFetcher;
  /** Fetch the scene.json DESCRIPTOR. Deliberately separate from `fetchAsset`
   *  so the caller can leave it uncached — `ogsContentPacks` fetches scene.json
   *  with `fetchAamFile` (cache:'no-store') for the same reason. A descriptor is
   *  small and edited in place, so a same-size edit would slip past a
   *  size-versioned cache key and a stale copy would silently shadow the server
   *  (CLAUDE.md: the server is the source of truth, never a local cache).
   *  Defaults to `fetchAsset`, then plain `fetch`. */
  fetchDescriptor?: AssetFetcher;
  /** Byte size by filename, for the cache's size-versioned key. Matched on
   *  basename, so a full URL is an acceptable argument. */
  sizeOf?: (filename: string) => number | undefined;
}

/** Load the static mesh geometry the streaming lift needs, for EITHER a
 *  single_region (one rig-driven branch) or multi_region (head+body) scene —
 *  mirrors the inference path's `loadScene` mode discrimination. No silent
 *  fallback / fake data: a bad mode or a failed fetch throws (loud failure).
 *
 *  single_region scene.json: top-level { mesh_json, mesh_bin } (+ subject).
 *  multi_region  scene.json: { branches: { head:{mesh_json,mesh_bin}, body:{…} } }.
 *
 *  `resolveAssetUrl` (required) maps each sub-asset filename → its server URL.
 *  There is no local/bundled fallback — streaming assets come only from the server. */
export async function loadStreamScene(
  sceneJsonUrl: string,
  resolveAssetUrl: AssetUrlResolver,
  opts: StreamSceneOptions = {},
): Promise<StreamScene> {
  // Descriptor: uncached by default (see StreamSceneOptions.fetchDescriptor).
  const scene = await fetchJson(opts.fetchDescriptor ?? opts.fetchAsset ?? plainFetch,
                                sceneJsonUrl, opts.sizeOf?.(sceneJsonUrl));
  const resolve = resolveAssetUrl;
  const subject = scene.subject ?? "stream";

  if (scene.mode === "single_region") {
    if (!scene.mesh_json || !scene.mesh_bin) {
      throw new Error(`loadStreamScene: single_region scene needs mesh_json/mesh_bin at ${sceneJsonUrl}`);
    }
    const manifest = await loadMesh(resolve, opts, scene.mesh_json, scene.mesh_bin);
    // The lone branch maps onto the head wire slot (verts tag 1/7, untile buf 0/1).
    return { subject, mode: "single_region", branches: { head: { manifest, rigDriven: true } }, order: ["head"] };
  }

  if (scene.mode === "multi_region" && scene.branches?.head && scene.branches?.body) {
    const [head, body] = await Promise.all([
      loadMesh(resolve, opts, scene.branches.head.mesh_json, scene.branches.head.mesh_bin),
      loadMesh(resolve, opts, scene.branches.body.mesh_json, scene.branches.body.mesh_bin),
    ]);
    return {
      subject, mode: "multi_region",
      branches: { head: { manifest: head, rigDriven: true }, body: { manifest: body, rigDriven: false } },
      order: ["head", "body"],
    };
  }

  throw new Error(`loadStreamScene: unsupported scene at ${sceneJsonUrl} ` +
    `(need mode "single_region" with mesh_json/mesh_bin, or "multi_region" with head+body branches)`);
}
